import { useState } from 'react';
import { Search, Star } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Card, CardHeader } from '../_primitives';

export interface TopicCoverageItem {
  keyword: string;
  /** Times the keyword has been used in the doc. */
  used: number;
  /** Target number of usages. */
  target: number;
  /** Search volume / month. */
  sv?: number;
  /** Keyword difficulty 0–100. */
  kd?: number;
  /** Cost per click in USD. */
  cpc?: number;
  /** When true, renders the primary-keyword star marker. */
  primary?: boolean;
}

export interface TopicCoverageCardProps {
  items: TopicCoverageItem[];
  /** Optional override for the "x/3" header indicator; defaults to hit count. */
  hitOverride?: { hit: number; total: number };
}

type Tone = 'success' | 'warning' | 'neutral';

const TONE_BG: Record<Tone, string> = {
  success: 'bg-teal-light border-teal/30',
  warning: 'bg-[hsl(var(--status-warning-bg))] border-[hsl(var(--status-warning)/0.3)]',
  neutral: 'bg-warm-bg border-border',
};

const TONE_FG: Record<Tone, string> = {
  success: 'text-teal-dark',
  warning: 'text-[hsl(var(--status-warning))]',
  neutral: 'text-foreground',
};

const TONE_ACCENT: Record<Tone, string> = {
  success: 'bg-[hsl(var(--status-success))]',
  warning: 'bg-[hsl(var(--status-warning))]',
  neutral: 'bg-muted-foreground/40',
};

const TONE_ACCENT_TEXT: Record<Tone, string> = {
  success: 'text-[hsl(var(--status-success))]',
  warning: 'text-[hsl(var(--status-warning))]',
  neutral: 'text-muted-foreground',
};

export function TopicCoverageCard({ items, hitOverride }: TopicCoverageCardProps) {
  const [showMetrics, setShowMetrics] = useState(true);
  const completed = items.filter((d) => d.used >= d.target).length;
  const hit = hitOverride?.hit ?? completed;
  const total = hitOverride?.total ?? items.length;

  return (
    <Card railSection="coverage">
      <CardHeader
        icon={<Search className="size-3.5" />}
        title="Topic coverage"
        trailing={
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-muted-foreground">
              {hit}/{total} hit
            </span>
            <button
              type="button"
              onClick={() => setShowMetrics((v) => !v)}
              aria-pressed={showMetrics}
              className={cn(
                'inline-flex items-center px-1.5 py-0.5 text-[10.5px] rounded border cursor-pointer transition-colors',
                showMetrics
                  ? 'bg-warm-bg text-foreground border-border'
                  : 'bg-transparent text-muted-foreground border-border hover:text-foreground',
              )}
            >
              SV/KD/CPC
            </button>
          </div>
        }
      />
      <div className="px-3 pb-3 flex flex-col gap-1.5">
        {items.map((d) => {
          const ratio = d.target === 0 ? 0 : Math.min(d.used, d.target) / d.target;
          const over = d.used > d.target;
          const done = d.used >= d.target;
          const tone: Tone = over ? 'warning' : done ? 'success' : 'neutral';
          return (
            <div
              key={d.keyword}
              className={cn('rounded-md border px-2.5 py-1.5', TONE_BG[tone])}
            >
              <div className="flex items-center gap-1.5">
                {d.primary && <Star className="size-3 fill-coral text-coral shrink-0" />}
                <span className={cn('flex-1 min-w-0 truncate text-[12.5px] font-semibold', TONE_FG[tone])}>
                  {d.keyword}
                </span>
                <span className={cn('font-mono text-[11.5px] font-bold', TONE_ACCENT_TEXT[tone])}>
                  {d.used}/{d.target}
                </span>
              </div>
              <div className="h-[3px] bg-foreground/[0.06] rounded-full mt-1.5 overflow-hidden">
                <div
                  className={cn('h-full transition-[width] duration-300', TONE_ACCENT[tone])}
                  style={{ width: over ? '100%' : `${ratio * 100}%` }}
                />
              </div>
              {showMetrics && (d.sv != null || d.kd != null || d.cpc != null) && (
                <div className="flex gap-2.5 mt-1.5 text-[10.5px] text-muted-foreground">
                  {d.sv != null && (
                    <span>
                      <span className="font-semibold text-foreground">SV</span>{' '}
                      {d.sv.toLocaleString()}
                    </span>
                  )}
                  {d.kd != null && (
                    <span>
                      <span className="font-semibold text-foreground">KD</span> {d.kd}
                    </span>
                  )}
                  {d.cpc != null && (
                    <span>
                      <span className="font-semibold text-foreground">CPC</span> ${d.cpc}
                    </span>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {items.length === 0 && (
          <div className="text-center text-[12px] text-muted-foreground py-4">
            No keywords tracked.
          </div>
        )}
      </div>
    </Card>
  );
}
